import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const LEVEL_OPTIONS = [
  { value: "", label: "Any Level" },
  { value: "BEGINNER", label: "Beginner" },
  { value: "INTERMEDIATE", label: "Intermediate" },
  { value: "ADVANCED", label: "Advanced" },
];

export default function GameCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    game_datetime: "",
    location: "",
    min_players: 4,
    max_players: 4,
    level_preference: "",
  });

  const mutation = useMutation({
    mutationFn: (data) => api.post("/games", data),
    onSuccess: (res) => {
      toast.success("Game posted");
      navigate(`/games/${res.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.game_datetime) {
      toast.error("Date and time is required");
      return;
    }
    const payload = {
      ...form,
      min_players: parseInt(form.min_players, 10) || 4,
      max_players: parseInt(form.max_players, 10) || 4,
      level_preference: form.level_preference || null,
    };
    mutation.mutate(payload);
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="game-create-page">
      <Link
        to="/games"
        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-lv-muted hover:text-bronze transition-colors mb-8"
        data-testid="game-create-back"
      >
        <ArrowLeft size={14} />
        Games
      </Link>

      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-8">
        Post a Game
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
            Title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={handleChange("title")}
            placeholder="e.g. Sunday doubles at Los Establos"
            className="lv-input"
            data-testid="game-create-title"
          />
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
            Date & Time
          </label>
          <input
            type="datetime-local"
            value={form.game_datetime}
            onChange={handleChange("game_datetime")}
            className="lv-input"
            data-testid="game-create-datetime"
          />
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
            Location
          </label>
          <input
            type="text"
            value={form.location}
            onChange={handleChange("location")}
            placeholder="e.g. Los Establos Padel Club, Court 3"
            className="lv-input"
            data-testid="game-create-location"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
              Min Players
            </label>
            <input
              type="number"
              min="2"
              max="20"
              value={form.min_players}
              onChange={handleChange("min_players")}
              className="lv-input"
              data-testid="game-create-min-players"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
              Max Players
            </label>
            <input
              type="number"
              min="2"
              max="20"
              value={form.max_players}
              onChange={handleChange("max_players")}
              className="lv-input"
              data-testid="game-create-max-players"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
            Level Preference
          </label>
          <select
            value={form.level_preference}
            onChange={handleChange("level_preference")}
            className="lv-input"
            data-testid="game-create-level"
          >
            {LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-lv-muted block mb-1.5">
            Notes (optional)
          </label>
          <textarea
            value={form.description}
            onChange={handleChange("description")}
            rows={3}
            placeholder="Any details about the game…"
            className="lv-input resize-none"
            data-testid="game-create-description"
          />
        </div>

        <button
          type="submit"
          disabled={mutation.isPending}
          className="lv-btn-primary w-full min-h-[48px] mt-4"
          data-testid="game-create-submit"
        >
          {mutation.isPending ? "Posting\u2026" : "Post Game"}
        </button>
      </form>
    </div>
  );
}
